import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '@core/store'
import {
  buildDiffColorMap,
  compareIndexes,
  DIFF_COLORS,
  diffToCsv,
  fieldLabel,
  indexModel,
  prettyType,
  type EntitySnapshot,
  type IfcDiff,
  type IndexProgress,
} from '@viewer/lib/ifcDiff'
import {
  type ElementoPorCasar,
  type ResultadoEmparelhamento,
  emparelharPorConteudo,
  resumoEmparelhamento,
} from '@viewer/lib/diffContentMatch'
import {
  type ContextoDoMapa,
  type EntradaIdentidade,
  aplicaveis,
  chavesPor,
  construirMapa,
  cruzarPorChave,
  especValida,
  lerMapa,
  reindexarPorIdentidade,
  resumoIdentidade,
} from '@viewer/lib/diffIdentidade'
import { viewerApi } from '@viewer/lib/viewerApi'
import { schemaLabel } from '@viewer/lib/ifcSchema'

/**
 * Floating panel for "compare two IFC models". Lives over the canvas
 * (not blocking interaction with toolbar / view cube) and uses the
 * parallel web-ifc parser already attached to each loaded model.
 *
 * Picker A vs picker B → "Comparar" → counts by IFC type + scrollable
 * delta list. Clicking a row hands the right-side expressID to the
 * existing selection api so the user sees the element in context.
 */
// An element counts as "moved" when its bbox centre shifts more than this
// (in mm). ThatOpen Fragments works in metres, so world distance × 1000 = mm.
const MOVED_THRESHOLD_MM = 5
const WORLD_TO_MM = 1000

/** Detect elements whose bbox centre moved between the two versions.
 *  Returns gid -> displacement (mm) for those over the threshold. Two
 *  getBoxes calls total (one per model), so it's cheap. */
/** Junta os candidatos a emparelhamento por conteúdo: o que ficou por
 *  casar de um lado e do outro, com o centro da caixa envolvente para
 *  poder desempatar.
 *
 *  Duas chamadas ao viewer, uma por modelo, como já se faz para
 *  detetar os movidos. */
async function candidatosPorConteudo(
  leftId: string,
  rightId: string,
  left: Map<string, EntitySnapshot>,
  right: Map<string, EntitySnapshot>,
  removidos: readonly { globalId: string }[],
  acrescentados: readonly { globalId: string }[],
): Promise<{ antes: ElementoPorCasar[]; depois: ElementoPorCasar[] }> {
  const montar = async (
    modelId: string,
    mapa: Map<string, EntitySnapshot>,
    gids: readonly { globalId: string }[],
  ): Promise<ElementoPorCasar[]> => {
    const snaps = gids
      .map((g) => mapa.get(g.globalId))
      .filter((s): s is EntitySnapshot => Boolean(s))
    if (snaps.length === 0) return []
    let centros: (number[] | null)[] = []
    try {
      centros = await viewerApi.getWorldBoxCenters(
        modelId,
        snaps.map((s) => s.expressID),
      )
    } catch {
      // Sem centros o emparelhamento continua a funcionar pelo
      // conteúdo; só perde o desempate por posição.
      centros = []
    }
    return snaps.map((s, i) => {
      const c = centros[i]
      return {
        globalId: s.globalId,
        schemaType: s.schemaType,
        name: s.name,
        objectType: s.objectType,
        tag: s.tag,
        predefinedType: s.predefinedType,
        container: s.container,
        centro: c
          ? {
              x: c[0] * WORLD_TO_MM,
              y: c[1] * WORLD_TO_MM,
              z: c[2] * WORLD_TO_MM,
            }
          : undefined,
      }
    })
  }

  const [antes, depois] = await Promise.all([
    montar(leftId, left, removidos),
    montar(rightId, right, acrescentados),
  ])
  return { antes, depois }
}

async function computeMoved(
  leftId: string,
  rightId: string,
  left: Map<string, EntitySnapshot>,
  right: Map<string, EntitySnapshot>,
): Promise<Map<string, number>> {
  const moved = new Map<string, number>()
  const gids: string[] = []
  const lids: number[] = []
  const rids: number[] = []
  for (const [gid, l] of left) {
    const r = right.get(gid)
    if (!r) continue
    gids.push(gid)
    lids.push(l.expressID)
    rids.push(r.expressID)
  }
  if (gids.length === 0) return moved
  const [lc, rc] = await Promise.all([
    viewerApi.getWorldBoxCenters(leftId, lids),
    viewerApi.getWorldBoxCenters(rightId, rids),
  ])
  for (let i = 0; i < gids.length; i++) {
    const a = lc[i]
    const b = rc[i]
    if (!a || !b) continue
    const dx = a[0] - b[0]
    const dy = a[1] - b[1]
    const dz = a[2] - b[2]
    const mm = Math.sqrt(dx * dx + dy * dy + dz * dz) * WORLD_TO_MM
    if (mm > MOVED_THRESHOLD_MM) moved.set(gids[i], Math.round(mm))
  }
  return moved
}

export default function IfcDiffPanel() {
  const open = useAppStore((s) => s.ifcDiffOpen)
  const setOpen = useAppStore((s) => s.setIfcDiffOpen)
  const models = useAppStore((s) => s.models)

  // A escolha guarda-se só quando alguém escolhe. Enquanto ninguém
  // escolher, vale o primeiro modelo de cada lado, calculado aqui e
  // não escrito em estado por um efeito: escrever estado no corpo de
  // um efeito encadeia renders, e era o que o lint apontava.
  const [escolhaEsquerda, setLeftId] = useState<string>('')
  const [escolhaDireita, setRightId] = useState<string>('')
  // Uma escolha de um modelo que entretanto foi fechado deixa de valer.
  const carregado = (id: string) => models.some((m) => m.id === id)
  const leftId = carregado(escolhaEsquerda)
    ? escolhaEsquerda
    : (models[0]?.id ?? '')
  const rightId = carregado(escolhaDireita)
    ? escolhaDireita
    : (models[1]?.id ?? '')
  /** Identidade dos dois modelos comparados: id e SHA-256. Muda quando um
   *  deles é fechado ou reaberto com outro conteúdo (o id é o nome do
   *  ficheiro, por isso uma versão nova com o mesmo nome tem o mesmo id). */
  const assinaturaDe = (a: string, b: string): string =>
    [a, b]
      .map((id) => {
        const m = models.find((x) => x.id === id)
        return m ? `${m.id} ${m.sha256 ?? ''}` : '(fechado)'
      })
      .join('\n')
  const [progress, setProgress] = useState<IndexProgress | null>(null)
  // O emparelhamento por conteúdo é opcional: custa duas leituras de
  // caixas envolventes e só interessa quando o exportador mexeu nos
  // GlobalId. Fica ligado por omissão porque é esse o caso que estraga
  // o relatório sem avisar.
  const [casarPorConteudo, setCasarPorConteudo] = useState(true)
  const [emparelhamentoGuardado, setEmparelhamento] =
    useState<ResultadoEmparelhamento | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [comparacao, setComparacao] = useState<{
    diff: IfcDiff
    assinatura: string
  } | null>(null)
  // A comparação só vale enquanto os dois modelos comparados forem os
  // que estão carregados. Fechar ou reabrir um deles tira-a do painel e,
  // pelo efeito de pintura abaixo, do 3D.
  const diff =
    comparacao &&
    comparacao.assinatura ===
      assinaturaDe(comparacao.diff.leftModelId, comparacao.diff.rightModelId)
      ? comparacao.diff
      : null
  const emparelhamento = diff ? emparelhamentoGuardado : null
  // Identidade: onde está a chave que o autor mantém, e o mapa de
  // pares já revistos que se queira voltar a aplicar. Ver
  // `diffIdentidade.ts` para o porquê de tudo isto.
  const [especChave, setEspecChave] = useState('')
  const [mapaLido, setMapaLido] = useState<{
    pares: Map<string, string>
    nome: string
  } | null>(null)
  const [identidadeGuardada, setIdentidade] = useState<{
    porChave: number
    doMapa: number
    porAplicar: number
    repetidos: number
    colisoes: number
  } | null>(null)
  const identidade = diff ? identidadeGuardada : null
  const ficheiroMapaRef = useRef<HTMLInputElement>(null)
  const [show3d, setShow3d] = useState(true)
  const [withProps, setWithProps] = useState(true)
  const [section, setSection] = useState<
    'modified' | 'added' | 'removed' | 'unchanged'
  >('modified')
  // Tracks whether we currently have diff colours painted, so we only
  // issue a clear (a full repaint) when there's something to undo.
  const appliedRef = useRef(false)

  // Paint / unpaint the diff in 3D. Active only while the panel is open,
  // a diff exists, and "Ver no 3D" is on. Any other combination clears.
  useEffect(() => {
    if (open && diff && show3d) {
      void viewerApi.applyDiffColors(buildDiffColorMap(diff))
      appliedRef.current = true
    } else if (appliedRef.current) {
      void viewerApi.clearDiffColors()
      appliedRef.current = false
    }
  }, [open, diff, show3d])

  // Safety net: drop the overlay if the panel unmounts mid-comparison.
  useEffect(
    () => () => {
      if (appliedRef.current) void viewerApi.clearDiffColors()
    },
    [],
  )

  const onFocusChanges = () => {
    if (!diff) return
    const targets: { modelId: string; localId: number }[] = []
    for (const e of diff.removed) {
      if (e.leftExpressID != null)
        targets.push({ modelId: diff.leftModelId, localId: e.leftExpressID })
    }
    for (const e of [...diff.added, ...diff.modified]) {
      if (e.rightExpressID != null)
        targets.push({ modelId: diff.rightModelId, localId: e.rightExpressID })
    }
    void viewerApi.fitToItems(targets)
  }

  const onExportCsv = () => {
    if (!diff) return
    const clean = (s: string) =>
      s.replace(/\.ifc(zip)?$/i, '').replace(/[^\w.-]+/g, '_').slice(0, 40)
    const nameA =
      models.find((m) => m.id === diff.leftModelId)?.fileName ?? 'A'
    const nameB =
      models.find((m) => m.id === diff.rightModelId)?.fileName ?? 'B'
    // Prepend a UTF-8 BOM so Excel reads accented text correctly.
    const blob = new Blob(['﻿' + diffToCsv(diff)], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `diff_${clean(nameA)}_vs_${clean(nameB)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  /** O contexto a que um mapa de identidade pertence: os dois
   *  ficheiros e o esquema sob que foi escrito. Sem isto, um mapa
   *  aplicado ao par errado daria um relatório limpo e falso. */
  const contextoDoMapa = (): ContextoDoMapa | null => {
    const a = models.find((m) => m.id === leftId)?.sha256
    const b = models.find((m) => m.id === rightId)?.sha256
    if (!a || !b) return null
    return especValida(especChave)
      ? { antes: a, depois: b, esquema: 'chave-autoral', especChave: especChave.trim() }
      : { antes: a, depois: b, esquema: 'globalid' }
  }

  const onGravarMapa = () => {
    const contexto = contextoDoMapa()
    if (!contexto || !emparelhamento) return
    const entradas: EntradaIdentidade[] = emparelhamento.pares.map((par) => ({
      antes: par.antes.globalId,
      depois: par.depois.globalId,
      razao: `conteúdo:${par.tipo}`,
    }))
    const mapa = construirMapa(contexto, entradas)
    const blob = new Blob([JSON.stringify(mapa, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'identidade.json'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const onCarregarMapa = async (ficheiro: File) => {
    const contexto = contextoDoMapa()
    if (!contexto) {
      setError('Escolhe os dois modelos antes de carregar um mapa.')
      return
    }
    try {
      const bruto: unknown = JSON.parse(await ficheiro.text())
      const lido = lerMapa(bruto, contexto)
      if (!lido.ok) {
        setMapaLido(null)
        setError(lido.motivo)
        return
      }
      setMapaLido({ pares: lido.pares, nome: ficheiro.name })
      setError(null)
    } catch {
      setMapaLido(null)
      setError('Não consegui ler esse ficheiro como JSON.')
    }
  }

  const onCompare = async () => {
    if (!leftId || !rightId) {
      setError('Escolhe dois modelos para comparar.')
      return
    }
    if (leftId === rightId) {
      setError('Os dois modelos têm de ser diferentes.')
      return
    }
    setError(null)
    setComparacao(null)
    setBusy(true)
    try {
      setProgress({ schemaType: 'A indexar A…', done: 0, total: 1 })
      const left = await indexModel(
        leftId,
        (p) => setProgress({ ...p, schemaType: `A · ${p.schemaType}` }),
        withProps,
      )
      setProgress({ schemaType: 'A indexar B…', done: 0, total: 1 })
      const right = await indexModel(
        rightId,
        (p) => setProgress({ ...p, schemaType: `B · ${p.schemaType}` }),
        withProps,
      )

      // Identidade, antes de comparar. O que aqui se faz é reescrever
      // as chaves do lado antigo para as do lado novo, quando há prova
      // de que é o mesmo elemento. Feito isto, a comparação corre como
      // sempre correu e vê um elemento alterado onde via um removido
      // mais um acrescentado.
      setProgress({ schemaType: 'A resolver identidades…', done: 0, total: 1 })
      const usaChave = especValida(especChave)
      const chavesA = chavesPor([...left.values()], usaChave ? especChave : null)
      const chavesB = chavesPor([...right.values()], usaChave ? especChave : null)
      const renomear = cruzarPorChave(chavesA.chaves, chavesB.chaves)
      const porChave = renomear.size
      let doMapa = 0
      let porAplicar = 0
      if (mapaLido) {
        const aplic = aplicaveis(
          mapaLido.pares,
          new Set(left.keys()),
          new Set(right.keys()),
        )
        porAplicar = aplic.porAplicar
        for (const [a, d] of aplic.aplicadas) {
          // A chave autoral tem precedência: é o que o autor mantém,
          // contra um par que alguém aceitou um dia.
          if (!renomear.has(a)) {
            renomear.set(a, d)
            doMapa += 1
          }
        }
      }
      const reindexado = reindexarPorIdentidade(left, renomear)

      // Movidos DEPOIS da identidade: os pares casados pela chave autoral
      // ou pelo mapa só se encontram no índice reindexado. Calculado
      // antes, um pilar casado pela chave que mudou de sítio saía igual.
      setProgress({ schemaType: 'A detetar movidos…', done: 0, total: 1 })
      const moved = await computeMoved(leftId, rightId, reindexado.indice, right)
      setIdentidade({
        porChave,
        doMapa,
        porAplicar,
        repetidos: chavesA.repetidos.length + chavesB.repetidos.length,
        colisoes: reindexado.colisoes,
      })

      setProgress(null)
      const result = compareIndexes(
        leftId,
        rightId,
        reindexado.indice,
        right,
        moved,
      )
      setComparacao({ diff: result, assinatura: assinaturaDe(leftId, rightId) })
      if (casarPorConteudo && (result.added.length > 0 || result.removed.length > 0)) {
        setProgress({
          schemaType: 'A procurar GlobalId trocados…',
          done: 0,
          total: 1,
        })
        const { antes, depois } = await candidatosPorConteudo(
          leftId,
          rightId,
          left,
          right,
          result.removed,
          result.added,
        )
        setEmparelhamento(emparelharPorConteudo(antes, depois))
      } else {
        setEmparelhamento(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao comparar.')
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  if (!open) return null

  // Resolve the two compared models for the result header (names + schema).
  const modelA = diff ? models.find((m) => m.id === diff.leftModelId) : null
  const modelB = diff ? models.find((m) => m.id === diff.rightModelId) : null
  const totalChanged = diff
    ? diff.summary.added + diff.summary.modified + diff.summary.removed
    : 0
  const totalCompared = diff
    ? totalChanged + diff.summary.unchanged
    : 0

  return (
    <div className="pointer-events-auto absolute right-3 top-14 z-20 flex max-h-[80%] w-[400px] flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-sm shadow-2xl">
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <span className="font-medium">Comparar IFC</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="ml-auto rounded text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          title="Fechar"
        >
          ✕
        </button>
      </div>

      {models.length < 2 ? (
        <div className="px-3 py-3 text-xs text-[var(--color-text-muted)]">
          Carrega pelo menos dois modelos IFC (botão “+ Adicionar” no
          canto superior esquerdo) para ativar a comparação.
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2 border-b border-[var(--color-border)] px-3 py-2 text-xs">
            <label className="flex flex-col gap-0.5">
              <span className="text-[var(--color-text-dim)]">A (origem)</span>
              <select
                value={leftId}
                onChange={(e) => setLeftId(e.target.value)}
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
              >
                <option value="">- escolher -</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fileName} · {schemaLabel(m.schemaVersion)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[var(--color-text-dim)]">B (destino)</span>
              <select
                value={rightId}
                onChange={(e) => setRightId(e.target.value)}
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
              >
                <option value="">- escolher -</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fileName} · {schemaLabel(m.schemaVersion)}
                  </option>
                ))}
              </select>
            </label>
            <label
              className="flex cursor-pointer items-center gap-1 text-[var(--color-text-muted)]"
              title="Inclui as propriedades de Psets/Qsets na comparação (ex.: FireRating, IsExternal, quantidades). Desliga para uma comparação mais rápida só de atributos em modelos muito grandes."
            >
              <input
                type="checkbox"
                checked={withProps}
                onChange={(e) => setWithProps(e.target.checked)}
                className="accent-[var(--color-accent)]"
              />
              Comparar propriedades (Psets/Qsets)
            </label>
            <button
              type="button"
              onClick={() => void onCompare()}
              disabled={busy || !leftId || !rightId || leftId === rightId}
              className="self-start rounded bg-[var(--color-accent)] px-3 py-1 font-medium text-[var(--color-bg)] disabled:opacity-50"
            >
              {busy ? 'A comparar…' : 'Comparar'}
            </button>
            {progress && (
              <p className="text-[var(--color-text-dim)]">
                {progress.schemaType}{' '}
                {progress.total > 0 && `(${progress.done}/${progress.total})`}
              </p>
            )}
            {error && <p className="text-[var(--color-error-text)]">{error}</p>}
          </div>

          {diff && (
            <div className="flex shrink-0 items-baseline gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
              <span
                className="max-w-[42%] truncate font-medium text-[var(--color-text)]"
                title={`A: ${modelA?.fileName ?? ''}`}
              >
                {modelA?.fileName ?? 'A'}
              </span>
              {modelA && (
                <span className="text-[9px] text-[var(--color-text-dim)]">
                  {schemaLabel(modelA.schemaVersion)}
                </span>
              )}
              <span className="text-[var(--color-text-dim)]">→</span>
              <span
                className="max-w-[42%] truncate font-medium text-[var(--color-text)]"
                title={`B: ${modelB?.fileName ?? ''}`}
              >
                {modelB?.fileName ?? 'B'}
              </span>
              {modelB && (
                <span className="text-[9px] text-[var(--color-text-dim)]">
                  {schemaLabel(modelB.schemaVersion)}
                </span>
              )}
              <span
                className="ml-auto whitespace-nowrap text-[10px] text-[var(--color-text-muted)]"
                title={`${totalChanged} alterações em ${totalCompared} elementos comparados`}
              >
                {totalChanged}/{totalCompared}
              </span>
            </div>
          )}

          <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-2 text-[10px]">
            <label className="flex items-center gap-2 text-[var(--color-text-muted)]">
              <span
                className="shrink-0"
                title="O GlobalId é o identificador que a ferramenta de autor regenera sempre que o elemento é redesenhado. Se o modelo trouxer um código que alguém mantém de propósito, comparar por esse código é mais fiável."
              >
                Chave
              </span>
              <input
                type="text"
                value={especChave}
                onChange={(e) => setEspecChave(e.target.value)}
                placeholder="GlobalId (ex.: Pset_Asset.AssetId ou Tag)"
                className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5 text-[10px] text-[var(--color-text)] placeholder:text-[var(--color-text-dim)]"
              />
            </label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => ficheiroMapaRef.current?.click()}
                className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                title="Voltar a aplicar pares de identidade gravados de uma comparação anterior destes dois ficheiros"
              >
                Carregar mapa
              </button>
              <input
                ref={ficheiroMapaRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) void onCarregarMapa(f)
                }}
              />
              {mapaLido && (
                <span className="truncate text-[var(--color-text-muted)]">
                  {mapaLido.nome}: {mapaLido.pares.size} pares
                  <button
                    type="button"
                    onClick={() => setMapaLido(null)}
                    className="ml-1 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
                    title="Deixar de aplicar este mapa"
                  >
                    (tirar)
                  </button>
                </span>
              )}
              {emparelhamento && emparelhamento.pares.length > 0 && (
                <button
                  type="button"
                  onClick={onGravarMapa}
                  className="ml-auto rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  title="Gravar os pares que esta comparação aceitou, para os rever e voltar a aplicar. O ficheiro fica preso ao SHA-256 dos dois modelos."
                >
                  Gravar mapa
                </button>
              )}
            </div>
            {identidade && (identidade.porChave > 0 || identidade.doMapa > 0 || identidade.repetidos > 0 || identidade.porAplicar > 0 || identidade.colisoes > 0) && (
              <p className="mt-1 text-[var(--color-text-dim)]">
                {resumoIdentidade(identidade)}
              </p>
            )}
          </div>

          {diff && (
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--color-border)] px-3 py-2 text-[10px]">
              <label
                className="flex cursor-pointer items-center gap-1 text-[var(--color-text-muted)]"
                title="Pinta no 3D: removidos a vermelho no modelo A, novos a verde e alterados a amarelo no modelo B."
              >
                <input
                  type="checkbox"
                  checked={show3d}
                  onChange={(e) => setShow3d(e.target.checked)}
                  className="accent-[var(--color-accent)]"
                />
                Ver no 3D
              </label>
              <label
                className="flex cursor-pointer items-center gap-1 text-[var(--color-text-muted)]"
                title="Procurar elementos que só mudaram de GlobalId. Vários exportadores regeneram os identificadores a cada exportação, e sem isto um modelo onde nada mudou aparece com tudo removido e tudo novo."
              >
                <input
                  type="checkbox"
                  checked={casarPorConteudo}
                  onChange={(e) => setCasarPorConteudo(e.target.checked)}
                  className="accent-[var(--color-accent)]"
                />
                GlobalId trocados
              </label>
              <LegendSwatch color={DIFF_COLORS.removed} label="Removido" />
              <LegendSwatch color={DIFF_COLORS.added} label="Novo" />
              <LegendSwatch color={DIFF_COLORS.modified} label="Alterado" />
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={onFocusChanges}
                  disabled={totalChanged === 0}
                  className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                  title="Enquadrar a câmara em todos os elementos novos, removidos e alterados"
                >
                  Focar nas diferenças
                </button>
                <button
                  type="button"
                  onClick={onExportCsv}
                  className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  title="Exportar a comparação para CSV (abre no Excel)"
                >
                  Exportar CSV
                </button>
              </div>
            </div>
          )}

          {diff && emparelhamento && (
            <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-2 text-[10px]">
              <p className="text-[var(--color-text-muted)]">
                {resumoEmparelhamento(emparelhamento)}
              </p>
              {emparelhamento.pares.length > 0 && (
                <ul className="mt-1 max-h-28 space-y-0.5 overflow-y-auto">
                  {emparelhamento.pares.slice(0, 200).map((p) => (
                    <li
                      key={p.antes.globalId}
                      className="flex items-baseline gap-1 text-[var(--color-text-dim)]"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {p.depois.name ?? prettyType(p.depois.schemaType)}
                      </span>
                      <span className="shrink-0 text-[var(--color-text-muted)]">
                        {p.tipo === 'renomeado-e-movido'
                          ? `GlobalId novo, moveu ${Math.round(p.deslocamentoMm ?? 0)} mm`
                          : 'GlobalId novo'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {emparelhamento.pares.length > 200 && (
                <p className="mt-1 text-[var(--color-text-muted)]">
                  Mostrados os primeiros 200.
                </p>
              )}
            </div>
          )}

          {diff && (
            <DiffResults
              diff={diff}
              section={section}
              setSection={setSection}
            />
          )}
        </>
      )}
    </div>
  )
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1 text-[var(--color-text-muted)]">
      <span
        className="inline-block h-2.5 w-2.5 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  )
}

function DiffResults({
  diff,
  section,
  setSection,
}: {
  diff: IfcDiff
  section: 'modified' | 'added' | 'removed' | 'unchanged'
  setSection: (s: 'modified' | 'added' | 'removed' | 'unchanged') => void
}) {
  const items = useMemo(() => {
    if (section === 'modified') return diff.modified
    if (section === 'added') return diff.added
    if (section === 'removed') return diff.removed
    return diff.unchanged
  }, [diff, section])

  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    if (!q) return items
    return items.filter(
      (it) =>
        (it.name ?? '').toLowerCase().includes(q) ||
        prettyType(it.schemaType).toLowerCase().includes(q) ||
        it.globalId.toLowerCase().includes(q),
    )
  }, [items, q])

  const onRowClick = (
    expressID: number | null,
    modelId: string | null,
  ) => {
    if (expressID == null || modelId == null) return
    // expressID == localId in the Fragments world; hand off to the
    // existing highlight/selection api so the user sees it in 3D.
    void viewerApi.selectByLocalId(modelId, expressID, true)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 gap-1 border-b border-[var(--color-border)] px-3 py-2 text-xs">
        <SectionTab
          label={`Alterados (${diff.summary.modified})`}
          active={section === 'modified'}
          onClick={() => setSection('modified')}
          color="amber"
        />
        <SectionTab
          label={`Novos (${diff.summary.added})`}
          active={section === 'added'}
          onClick={() => setSection('added')}
          color="emerald"
        />
        <SectionTab
          label={`Removidos (${diff.summary.removed})`}
          active={section === 'removed'}
          onClick={() => setSection('removed')}
          color="red"
        />
        <SectionTab
          label={`Iguais (${diff.summary.unchanged})`}
          active={section === 'unchanged'}
          onClick={() => setSection('unchanged')}
          color="neutral"
        />
      </div>
      <div className="border-b border-[var(--color-border)] px-3 py-2 text-[10px] text-[var(--color-text-muted)]">
        {diff.byType.length === 0
          ? '-'
          : diff.byType.slice(0, 6).map((row) => (
              <span key={row.schemaType} className="mr-2 inline-block">
                <strong className="text-[var(--color-text)]">
                  {prettyType(row.schemaType)}
                </strong>
                {row.added > 0 && (
                  <span className="ml-1 text-[var(--color-ok-text)]">+{row.added}</span>
                )}
                {row.modified > 0 && (
                  <span className="ml-1 text-[var(--color-warn-text)]">~{row.modified}</span>
                )}
                {row.removed > 0 && (
                  <span className="ml-1 text-[var(--color-error-text)]">−{row.removed}</span>
                )}
              </span>
            ))}
      </div>
      <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-1.5">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Procurar por nome, tipo ou GlobalId…"
          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] text-[var(--color-text)] placeholder:text-[var(--color-text-dim)]"
        />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto text-xs">
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-[var(--color-text-dim)]">
            {q ? 'Sem resultados para a procura.' : 'Nenhum.'}
          </li>
        ) : (
          filtered.map((it) => {
            const targetExpress =
              section === 'removed' ? it.leftExpressID : it.rightExpressID
            const targetModel =
              section === 'removed' ? diff.leftModelId : diff.rightModelId
            return (
              <li
                key={`${section}-${it.globalId}`}
                className="border-b border-[var(--color-border-subtle)]"
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onRowClick(targetExpress, targetModel)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onRowClick(targetExpress, targetModel)
                    }
                  }}
                  className="block w-full cursor-pointer px-3 py-2 text-left hover:bg-[var(--color-panel-elevated)]"
                  title={
                    targetExpress != null ? 'Clica para selecionar no 3D' : ''
                  }
                >
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] text-[var(--color-text-dim)]">
                      {prettyType(it.schemaType)}
                    </span>
                    <span className="font-medium">
                      {it.name ?? '(sem nome)'}
                    </span>
                  </div>
                  {it.fields.length > 0 && <FieldList fields={it.fields} />}
                  <div className="mt-1 font-mono text-[9px] text-[var(--color-text-dim)]">
                    {it.globalId}
                  </div>
                </div>
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}

// Per-element list of changed fields, capped so an element with dozens of
// changed properties doesn't flood the row. "+N mais" expands in place; the
// expander stops propagation so it doesn't also trigger the row selection.
const FIELD_CAP = 6
function FieldList({
  fields,
}: {
  fields: { name: string; from: unknown; to: unknown }[]
}) {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? fields : fields.slice(0, FIELD_CAP)
  const hidden = fields.length - shown.length
  return (
    <ul className="mt-1 flex flex-col gap-0.5 text-[11px]">
      {shown.map((f) => (
        <li key={f.name} className="flex flex-wrap items-baseline gap-1">
          {f.name.startsWith('prop:') && (
            <span
              className="rounded bg-[var(--color-accent-bg)] px-1 text-[8px] uppercase tracking-wide text-[var(--color-accent)]"
              title="Propriedade de Pset/Qset"
            >
              pset
            </span>
          )}
          <span className="text-[var(--color-text-muted)]">
            {fieldLabel(f.name)}:
          </span>
          <span className="rounded bg-red-500/15 px-1 text-[var(--color-error-text)] line-through">
            {String(f.from ?? '-')}
          </span>
          <span className="text-[var(--color-text-dim)]">→</span>
          <span className="rounded bg-emerald-500/15 px-1 text-[var(--color-ok-text)]">
            {String(f.to ?? '-')}
          </span>
        </li>
      ))}
      {hidden > 0 && (
        <li>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(true)
            }}
            className="text-[10px] text-[var(--color-accent)] hover:underline"
          >
            +{hidden} mais
          </button>
        </li>
      )}
    </ul>
  )
}

function SectionTab({
  label,
  active,
  onClick,
  color,
}: {
  label: string
  active: boolean
  onClick: () => void
  color: 'amber' | 'emerald' | 'red' | 'neutral'
}) {
  const palette: Record<typeof color, string> = {
    amber: 'border-[var(--color-warn-border)] text-[var(--color-warn-text)] bg-[var(--color-accent-bg)]',
    emerald: 'border-[var(--color-ok-border)] text-[var(--color-ok-text)] bg-emerald-500/10',
    red: 'border-[var(--color-error-border)] text-[var(--color-error-text)] bg-red-500/10',
    neutral: 'border-[var(--color-border)] text-[var(--color-text-muted)]',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded border px-2 py-0.5 ${
        active
          ? palette[color]
          : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:text-[var(--color-text)]'
      }`}
    >
      {label}
    </button>
  )
}
